<template>
    <main class="more-page" aria-labelledby="more-page-title">
        <div class="more-page__inner">
            <header class="more-page__header">
                <div>
                    <p class="more-page__eyebrow">Навигация</p>
                    <h1 id="more-page-title" class="more-page__title">Ещё</h1>
                    <p class="more-page__subtitle">
                        Разделы собраны по рабочим направлениям.
                    </p>
                </div>
            </header>

            <nav class="more-sections" aria-label="Все разделы Аргус">
                <section
                    v-for="section in navigationSections"
                    :key="section.key"
                    class="more-section"
                    :class="{'more-section--active': nodeContainsActive(section)}"
                >
                    <button
                        type="button"
                        class="more-section__toggle"
                        :aria-expanded="String(isNavigationGroupOpen(section))"
                        @click="toggleNavigationGroup(section)"
                    >
                        <span class="more-section__icon" aria-hidden="true">
                            <i :class="section.icon"></i>
                        </span>
                        <span class="more-section__copy">
                            <strong>{{ section.label }}</strong>
                            <small>{{ countLinks(section) }} {{ sectionCountLabel(section) }}</small>
                        </span>
                        <i
                            class="fas more-section__chevron"
                            :class="isNavigationGroupOpen(section) ? 'fa-chevron-up' : 'fa-chevron-down'"
                            aria-hidden="true"
                        ></i>
                    </button>

                    <div v-show="isNavigationGroupOpen(section)" class="more-section__body">
                        <template v-for="item in section.items">
                            <div
                                v-if="item.kind === 'group'"
                                :key="item.key"
                                class="more-subgroup"
                            >
                                <button
                                    type="button"
                                    class="more-subgroup__toggle"
                                    :class="{'more-subgroup__toggle--active': nodeContainsActive(item)}"
                                    :aria-expanded="String(isNavigationGroupOpen(item))"
                                    @click="toggleNavigationGroup(item)"
                                >
                                    <span class="more-subgroup__icon" aria-hidden="true">
                                        <i :class="item.icon"></i>
                                    </span>
                                    <span>{{ item.label }}</span>
                                    <small>{{ countLinks(item) }}</small>
                                    <i
                                        class="fas more-subgroup__chevron"
                                        :class="isNavigationGroupOpen(item) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                        aria-hidden="true"
                                    ></i>
                                </button>

                                <div
                                    v-show="isNavigationGroupOpen(item)"
                                    class="more-subgroup__links"
                                >
                                    <router-link
                                        v-for="link in item.items"
                                        :key="link.key"
                                        :to="link.path"
                                        class="more-link more-link--nested"
                                        :class="{'more-link--active': isNavigationLinkActive(link)}"
                                    >
                                        <span class="more-link__icon" aria-hidden="true">
                                            <i :class="link.icon"></i>
                                        </span>
                                        <span class="more-link__label">{{ link.label }}</span>
                                        <i
                                            v-if="isFavourite(link)"
                                            class="fas fa-star more-link__favorite"
                                            title="Моя четвёртая вкладка"
                                            aria-label="Моя четвёртая вкладка"
                                        ></i>
                                        <i class="fas fa-chevron-right more-link__chevron" aria-hidden="true"></i>
                                    </router-link>
                                </div>
                            </div>

                            <router-link
                                v-else
                                :key="item.key"
                                :to="item.path"
                                class="more-link"
                                :class="{'more-link--active': isNavigationLinkActive(item)}"
                            >
                                <span class="more-link__icon" aria-hidden="true">
                                    <i :class="item.icon"></i>
                                </span>
                                <span class="more-link__label">{{ item.label }}</span>
                                <i
                                    v-if="isFavourite(item)"
                                    class="fas fa-star more-link__favorite"
                                    title="Моя четвёртая вкладка"
                                    aria-label="Моя четвёртая вкладка"
                                ></i>
                                <i class="fas fa-chevron-right more-link__chevron" aria-hidden="true"></i>
                            </router-link>
                        </template>
                    </div>
                </section>
            </nav>

            <section class="more-service" aria-labelledby="more-service-title">
                <h2 id="more-service-title" class="more-service__title">Аккаунт и приложение</h2>
                <div class="more-service__links">
                    <router-link to="/profile" class="more-service__link">
                        <i class="fas fa-user-circle" aria-hidden="true"></i>
                        <span>Мой профиль</span>
                    </router-link>
                    <router-link to="/about" class="more-service__link">
                        <i class="fas fa-info-circle" aria-hidden="true"></i>
                        <span>О приложении</span>
                    </router-link>
                </div>
            </section>
        </div>
    </main>
</template>

<script>
import {
    getAvailableWorkspaces,
    getModernNavigationSections,
    getWorkspaceByKey
} from '@/utils/workspaceRegistry';
import {
    getOpenNavigationGroups,
    saveOpenNavigationGroups
} from '@/utils/modernNavigationState';

function routePath(location) {
    if (!location) return '';
    if (typeof location === 'string') return location.split('?')[0];
    return location.path || '';
}

function routeQuery(location) {
    if (!location || typeof location !== 'string' || location.indexOf('?') === -1) {
        return {};
    }
    return location
        .split('?')[1]
        .split('&')
        .reduce((result, part) => {
            const pair = part.split('=');
            result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
            return result;
        }, {});
}

function pluralizeSection(count) {
    const value = Math.abs(Number(count) || 0);
    const mod100 = value % 100;
    const mod10 = value % 10;
    if (mod100 >= 11 && mod100 <= 14) return 'разделов';
    if (mod10 === 1) return 'раздел';
    if (mod10 >= 2 && mod10 <= 4) return 'раздела';
    return 'разделов';
}

export default {
    name: 'More',
    data() {
        return {
            openNavigationGroups: []
        };
    },
    computed: {
        user() {
            return this.$store.getters.currentUser || {};
        },
        navigation() {
            return this.$store.state.navigationExperience || {};
        },
        availableWorkspaces() {
            return getAvailableWorkspaces(this);
        },
        effectiveWorkspace() {
            const availableKeys = new Set(this.availableWorkspaces.map(item => item.key));
            const preferred = this.navigation.preferred_workspace_key;
            if (preferred && availableKeys.has(preferred)) {
                return getWorkspaceByKey(preferred);
            }
            const defaults = Array.isArray(this.navigation.department_default_workspace_keys)
                ? this.navigation.department_default_workspace_keys
                : [];
            const availableDefaults = defaults.filter(key => availableKeys.has(key));
            if (availableDefaults.length === 1) {
                return getWorkspaceByKey(availableDefaults[0]);
            }
            if (availableDefaults.length > 1) {
                return null;
            }
            return this.availableWorkspaces[0] || null;
        },
        navigationSections() {
            return getModernNavigationSections(this);
        }
    },
    watch: {
        'user.userId'() {
            this.restoreOpenNavigationGroups();
        }
    },
    mounted() {
        this.restoreOpenNavigationGroups();
        this.loadAccessContext();
    },
    methods: {
        loadAccessContext() {
            this.$store.dispatch('objectCards/fetchAccessSettings').catch(() => {});
            this.$store.dispatch('getEmployeeDirectorySettings').catch(() => {});
            if (!this.$can('read', 'objectsInProcess')) {
                this.$store.dispatch('getObjectsInProcess').catch(() => {});
            }
        },
        isRouteUnder(path) {
            if (!path) return false;
            return this.$route.path === path || this.$route.path.indexOf(`${path}/`) === 0;
        },
        isNavigationLinkActive(link) {
            if (!link) return false;
            const path = routePath(link.path);
            if (!this.isRouteUnder(path)) return false;
            const expectedQuery = routeQuery(link.path);
            return Object.keys(expectedQuery).every(key =>
                String(this.$route.query[key] || '') === String(expectedQuery[key])
            );
        },
        nodeContainsActive(node) {
            if (!node) return false;
            if (node.kind === 'link') {
                return this.isNavigationLinkActive(node);
            }
            return Array.isArray(node.items) &&
                node.items.some(item => this.nodeContainsActive(item));
        },
        isNavigationGroupOpen(node) {
            if (!node) return false;
            return this.nodeContainsActive(node) ||
                this.openNavigationGroups.indexOf(node.key) !== -1;
        },
        toggleNavigationGroup(node) {
            if (!node || !node.key) return;
            const index = this.openNavigationGroups.indexOf(node.key);
            if (index === -1) {
                this.openNavigationGroups = this.openNavigationGroups.concat(node.key);
            } else {
                this.openNavigationGroups = this.openNavigationGroups.filter(key => key !== node.key);
            }
            saveOpenNavigationGroups(this.user, this.openNavigationGroups);
        },
        restoreOpenNavigationGroups() {
            this.openNavigationGroups = getOpenNavigationGroups(this.user);
        },
        countLinks(node) {
            if (!node) return 0;
            if (node.kind === 'link') return 1;
            return (Array.isArray(node.items) ? node.items : [])
                .reduce((sum, item) => sum + this.countLinks(item), 0);
        },
        sectionCountLabel(section) {
            return pluralizeSection(this.countLinks(section));
        },
        isFavourite(link) {
            return Boolean(
                link &&
                this.effectiveWorkspace &&
                link.key === this.effectiveWorkspace.key
            );
        }
    }
};
</script>

<style scoped>
.more-page {
    min-height: 100%;
    color: #202936;
    background: #f5f7fa;
}

.more-page__inner {
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
    padding: 24px 20px 96px;
}

.more-page__header {
    margin-bottom: 22px;
}

.more-page__eyebrow,
.more-service__title {
    margin: 0 0 4px;
    color: #697586;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.more-page__title {
    margin: 0;
    color: #17202d;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.15;
}

.more-page__subtitle {
    margin: 7px 0 0;
    color: #697586;
}

.more-sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 12px;
}

.more-section {
    overflow: hidden;
    border: 1px solid #dce3eb;
    border-radius: 15px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(31, 42, 55, 0.05);
}

.more-section--active {
    border-color: #8db8ff;
    box-shadow: 0 5px 18px rgba(24, 97, 201, 0.11);
}

.more-section__toggle {
    display: flex;
    width: 100%;
    min-height: 72px;
    align-items: center;
    padding: 12px 14px;
    border: 0;
    color: #202936;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
}

.more-section__toggle:hover,
.more-section__toggle:focus {
    background: #f5f8fc;
    outline: none;
}

.more-section__icon {
    display: inline-flex;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    border-radius: 12px;
    color: #176ee2;
    background: #eaf2ff;
    font-size: 1.05rem;
}

.more-section__copy {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
}

.more-section__copy strong {
    overflow: hidden;
    font-size: 0.95rem;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.more-section__copy small {
    margin-top: 3px;
    color: #7a8594;
    font-size: 0.73rem;
}

.more-section__chevron,
.more-subgroup__chevron,
.more-link__chevron {
    flex: 0 0 auto;
    margin-left: 10px;
    color: #9aa4b2;
    font-size: 0.7rem;
}

.more-section__body {
    padding: 4px 10px 10px;
    border-top: 1px solid #edf0f4;
}

.more-subgroup {
    margin-top: 5px;
}

.more-subgroup__toggle {
    display: flex;
    width: 100%;
    min-height: 46px;
    align-items: center;
    padding: 8px 10px;
    border: 0;
    border-radius: 11px;
    color: #3f4b5b;
    background: #f7f9fb;
    font: inherit;
    font-size: 0.84rem;
    font-weight: 750;
    text-align: left;
    cursor: pointer;
}

.more-subgroup__toggle:hover,
.more-subgroup__toggle:focus {
    color: #0d57c8;
    background: #eef5ff;
    outline: none;
}

.more-subgroup__toggle--active {
    color: #0d57c8;
    background: #eef5ff;
}

.more-subgroup__icon {
    flex: 0 0 24px;
    width: 24px;
    color: #627086;
    text-align: center;
}

.more-subgroup__toggle > span:nth-child(2) {
    min-width: 0;
    flex: 1 1 auto;
    margin-left: 7px;
}

.more-subgroup__toggle small {
    color: #8994a4;
    font-size: 0.7rem;
}

.more-subgroup__links {
    margin: 4px 0 3px 18px;
    padding-left: 8px;
    border-left: 1px solid #dde5ef;
}

.more-link {
    display: flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    margin-top: 4px;
    padding: 7px 9px;
    border-radius: 11px;
    color: #313d4d;
    text-decoration: none;
}

.more-link:hover,
.more-link:focus {
    color: #0d57c8;
    background: #f1f6ff;
    outline: none;
    text-decoration: none;
}

.more-link--active {
    color: #0d57c8;
    background: #eaf2ff;
    font-weight: 800;
}

.more-link__icon {
    display: inline-flex;
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    color: #176ee2;
    background: #edf4ff;
    font-size: 0.82rem;
}

.more-link__label {
    min-width: 0;
    flex: 1 1 auto;
    margin-left: 10px;
    overflow: hidden;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.more-link__favorite {
    flex: 0 0 auto;
    margin-left: 8px;
    color: #f0a600;
    font-size: 0.74rem;
}

.more-service {
    margin-top: 24px;
}

.more-service__title {
    margin-left: 4px;
    margin-bottom: 9px;
}

.more-service__links {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.more-service__link {
    display: flex;
    min-height: 48px;
    align-items: center;
    padding: 9px 12px;
    border: 1px solid #dde3ea;
    border-radius: 12px;
    color: #354153;
    background: #fff;
    font: inherit;
    font-size: 0.84rem;
    font-weight: 700;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}

.more-service__link:hover,
.more-service__link:focus {
    color: #0d57c8;
    border-color: #9fc1f5;
    outline: none;
    text-decoration: none;
}

.more-service__link i {
    width: 22px;
    margin-right: 8px;
    color: #176ee2;
    text-align: center;
}

@media (max-width: 767.98px) {
    .more-page {
        min-height: calc(100vh - 56px);
        margin: 0 -0.5rem;
    }

    .more-page__inner {
        padding: 20px 14px 96px;
    }

    .more-page__title {
        font-size: 1.75rem;
    }

    .more-sections,
    .more-service__links {
        grid-template-columns: minmax(0, 1fr);
    }

    .more-section__toggle {
        min-height: 68px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .more-section,
    .more-link {
        scroll-behavior: auto;
    }
}
</style>
